import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiCall, getApiBaseUrl, authHeaders, withToken } from '../config';
import { notify } from '../ui/notify';

// Digitale Identitätsprüfung bei verlorener Garderobenmarke (ersetzt den Papierzettel).
// Fürs Handy des Mitarbeiters: (Gast beschreibt mündlich, wird NICHT erfasst – Philipp 20.09.) Foto + Nummer →
// Personendaten/Ausweis → zwei Unterschriften mit dem Finger. Datum, Uhrzeit, Station und
// Mitarbeiter kommen automatisch. KEIN Foto vom Ausweis (nur Art + Nummer).

// Unterschriftsfeld (Finger/Maus). Liefert per onChange eine PNG-Data-URL oder null.
const SignaturePad = ({ label, onChange }) => {
  const ref = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  const pos = (e) => {
    const c = ref.current; const r = c.getBoundingClientRect();
    const p = e.touches && e.touches[0] ? e.touches[0] : e;
    return { x: (p.clientX - r.left) * (c.width / r.width), y: (p.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = ref.current.getContext('2d'); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => {
    if (!drawing.current) return; e.preventDefault();
    const ctx = ref.current.getContext('2d'); const p = pos(e);
    ctx.lineWidth = 3; ctx.lineCap = 'round'; ctx.strokeStyle = '#111'; ctx.lineTo(p.x, p.y); ctx.stroke(); dirty.current = true;
  };
  const end = () => { if (!drawing.current) return; drawing.current = false; if (dirty.current) onChange(ref.current.toDataURL('image/png')); };
  const clear = () => { const c = ref.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); dirty.current = false; onChange(null); };
  return (
    <div className="mb-3">
      <div className="d-flex justify-content-between align-items-end">
        <label className="form-label fw-semibold mb-1">{label}</label>
        <button type="button" className="btn btn-link btn-sm p-0" onClick={clear}>löschen</button>
      </div>
      <canvas ref={ref} width={700} height={240}
        style={{ width: '100%', height: 150, border: '2px dashed #999', borderRadius: 10, background: '#fff', touchAction: 'none' }}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
    </div>
  );
};

// Handyfoto vor dem Hochladen verkleinern (max. 1600 px, JPEG) – spart Zeit im vollen Netz
const shrink = (file) => new Promise((resolve) => {
  const img = new Image(); const url = URL.createObjectURL(file);
  img.onload = () => {
    const max = 1600; const k = Math.min(1, max / Math.max(img.width, img.height));
    const c = document.createElement('canvas'); c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height); URL.revokeObjectURL(url);
    c.toBlob((b) => resolve(b ? new File([b], 'gepaeck.jpg', { type: 'image/jpeg' }) : file), 'image/jpeg', 0.82);
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  img.src = url;
});

// Geburtsdatum zum TIPPEN statt Kalender. Rückmeldung von der Station (21.09.2026): im Handy-Kalender
// muss man sich Monat für Monat bis z.B. 1979 zurückblättern – „takes ages". Jetzt: Ziffernblock,
// einfach 12031979 tippen → 12.03.1979 (Punkte kommen von selbst; auch 120379 geht). Liefert ISO oder ''.
const BirthdateInput = ({ value, onChange }) => {
  const toText = (iso) => (/^\d{4}-\d{2}-\d{2}$/.test(iso || '') ? iso.split('-').reverse().join('.') : '');
  const [text, setText] = useState(toText(value));
  const parse = (digits) => {
    if (digits.length !== 8) return null;
    const d = Number(digits.slice(0, 2)), m = Number(digits.slice(2, 4)), y = Number(digits.slice(4));
    const dt = new Date(y, m - 1, d); const now = new Date();
    if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d || y < 1900 || dt > now) return null;
    return { iso: `${digits.slice(4)}-${digits.slice(2, 4)}-${digits.slice(0, 2)}`, age: now.getFullYear() - y - (now < new Date(now.getFullYear(), m - 1, d) ? 1 : 0), label: dt.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' }) };
  };
  const format = (digits, adding) => {
    const p = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean);
    // Punkt nur beim Tippen anhängen – sonst ließe er sich nicht mehr löschen
    return p.join('.') + (adding && (digits.length === 2 || digits.length === 4) ? '.' : '');
  };
  const handle = (raw) => {
    const prev = text.replace(/\D/g, ''); const digits = raw.replace(/\D/g, '').slice(0, 8);
    setText(format(digits, digits.length > prev.length));
    const ok = parse(digits); onChange(ok ? ok.iso : '');
  };
  // Zweistelliges Jahr beim Verlassen ergänzen: 120379 → 12.03.1979, 120305 → 12.03.2005
  const blur = () => {
    const digits = text.replace(/\D/g, '');
    if (digits.length !== 6) return;
    const yy = Number(digits.slice(4)); const full = digits.slice(0, 4) + String(yy <= new Date().getFullYear() % 100 ? 2000 + yy : 1900 + yy);
    setText(format(full, false)); const ok = parse(full); onChange(ok ? ok.iso : '');
  };
  const digits = text.replace(/\D/g, ''); const ok = parse(digits);
  return (
    <>
      <input className={`form-control ${digits.length === 8 ? (ok ? 'is-valid' : 'is-invalid') : ''}`} inputMode="numeric" autoComplete="off" placeholder="TT.MM.JJJJ"
        value={text} onChange={(e) => handle(e.target.value)} onBlur={blur} />
      <div className={`form-text ${digits.length === 8 && !ok ? 'text-danger' : ''}`}>
        {ok ? `${ok.label} · ${ok.age} Jahre` : (digits.length === 8 ? 'Dieses Datum gibt es nicht – bitte prüfen.' : 'Nur Ziffern tippen, z. B. 12031979')}
      </div>
    </>
  );
};

const empty = (today, station) => ({
  station: station || '', tagNumber: '', dropoffDate: today, itemType: '', itemColor: '', itemFeatures: '', contents: '', descriptionMatches: false,
  guestName: '', guestStreet: '', guestCity: '', guestBirthdate: '', guestPhone: '', docType: '', docNumber: '', idVerified: false, notes: '',
});

const IdentityCheck = () => {
  const [ctx, setCtx] = useState({ station: '', staffName: '', today: '', itemTypes: [], docTypes: [] });
  const [step, setStep] = useState(0); // 0 = Übersicht, 1–3 = Assistent
  const [f, setF] = useState(empty('', ''));
  const [photo, setPhoto] = useState(null);
  const [photoUrl, setPhotoUrl] = useState('');
  const [guestSig, setGuestSig] = useState(null);
  const [staffSig, setStaffSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState({ manager: false, checks: [] });
  const [search, setSearch] = useState('');
  // Standardfilter: eigene Station + heute – beides umstellbar
  const [fStation, setFStation] = useState('');
  const [fDate, setFDate] = useState('');
  const [filtersReady, setFiltersReady] = useState(false);
  const [zoom, setZoom] = useState(null);
  const [detail, setDetail] = useState(null); // Einzelfall komplett (nur Leitung)
  const [q, setQ] = useState('');           // entprellter Suchtext → Suche läuft auf dem Server über ALLE Tage/Stationen
  useEffect(() => { const t = setTimeout(() => setQ(search.trim()), 350); return () => clearTimeout(t); }, [search]);

  const loadList = useCallback(async () => {
    if (!filtersReady) return;
    // Mit Suchtext: alle Tage + alle Stationen durchsuchen (sonst findet man den Fall von gestern nicht)
    const qs = q ? `station=all&date=all&q=${encodeURIComponent(q)}` : `station=${encodeURIComponent(fStation || 'all')}&date=${encodeURIComponent(fDate || 'all')}`;
    try { setList(await apiCall(`/identity-checks?${qs}`)); } catch (_) { /* Liste ist optional */ }
  }, [fStation, fDate, filtersReady, q]);
  const openDetail = async (id) => { try { setDetail(await apiCall(`/identity-checks/${id}`)); } catch (err) { notify(err.message || 'Konnte den Fall nicht laden'); } };
  useEffect(() => {
    apiCall('/identity-checks/context')
      .then((c) => { setCtx(c); setF(empty(c.today, c.station)); setFStation(c.station || ''); setFDate(c.today || ''); })
      .catch(() => {})
      .finally(() => setFiltersReady(true));
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const pick = (k, v) => setF({ ...f, [k]: f[k] === v ? '' : v });

  const onPhoto = async (e) => {
    const file = e.target.files && e.target.files[0]; if (!file) return;
    const small = await shrink(file); setPhoto(small); setPhotoUrl(URL.createObjectURL(small));
  };

  const canNext = () => {
    if (step === 1) return photo && f.tagNumber.trim() && f.descriptionMatches && f.station;
    if (step === 2) return f.guestName.trim().length > 1 && f.guestStreet.trim() && f.guestCity.trim() && f.guestBirthdate && f.docType && f.docNumber.trim().length >= 4 && f.idVerified;
    return true;
  };

  const submit = async () => {
    if (!guestSig || !staffSig) { notify('Bitte beide Unterschriften einholen.'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, typeof v === 'boolean' ? (v ? '1' : '') : v));
      fd.append('guestSignature', guestSig); fd.append('staffSignature', staffSig); fd.append('photo', photo);
      const res = await fetch(`${getApiBaseUrl()}/identity-checks`, { method: 'POST', headers: { ...authHeaders() }, body: fd });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Speichern fehlgeschlagen');
      notify(`Identitätsprüfung #I${body.id} gespeichert – Gepäck kann ausgegeben werden.`);
      setStep(0); setF(empty(ctx.today, ctx.station)); setPhoto(null); setPhotoUrl(''); setGuestSig(null); setStaffSig(null); loadList();
    } catch (err) { notify(err.message); }
    setBusy(false);
  };

  const chips = (items, key) => (
    <div className="d-flex flex-wrap gap-2">
      {items.map((v) => <button key={v} type="button" className={`btn ${f[key] === v ? 'btn-dark' : 'btn-outline-secondary'}`} onClick={() => pick(key, v)}>{v}</button>)}
    </div>
  );

  const shown = list.checks;
  const dDE = (iso) => (iso && /^\d{4}-\d{2}-\d{2}/.test(iso) ? iso.slice(0, 10).split('-').reverse().join('.') : iso || '–');
  const when = (s) => { try { return new Date(String(s).replace(' ', 'T') + 'Z').toLocaleString('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch (_) { return s; } };

  return (
    <div className="container py-3" style={{ maxWidth: 760 }}>
      <h4 className="mb-1">🪪 Identitätsprüfung</h4>
      <p className="text-muted">Wenn ein Gast seine Garderobenmarke / den Abholschein verloren hat. Ersetzt den Papierzettel.</p>

      {step === 0 && (
        <>
          <button className="btn btn-primary btn-lg w-100 mb-4" onClick={() => setStep(1)}>＋ Neue Identitätsprüfung starten</button>
          <div className="d-flex align-items-center gap-2 mb-2">
            <h6 className="mb-0 me-auto">Fälle ({shown.length})</h6>
          </div>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <select className="form-select form-select-sm" style={{ width: 'auto' }} value={fStation} onChange={(e) => setFStation(e.target.value)}>
              <option value="">alle Stationen</option>{['G1', 'G5', 'G8', 'G10'].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input type="date" className="form-control form-control-sm" style={{ width: 'auto' }} value={fDate} onChange={(e) => setFDate(e.target.value)} />
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setFDate('')} disabled={!fDate}>alle Tage</button>
            <input className="form-control form-control-sm" style={{ maxWidth: 280, flex: '1 1 200px' }} placeholder={list.manager ? 'Suchen: Nummer, Name, Ausweis, Adresse …' : 'Nummer oder Name suchen'} value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          {q && <div className="small text-muted mb-2">Suche „{q}“ läuft über <strong>alle Tage und Stationen</strong>.</div>}
          {!shown.length && q && <div className="text-muted small">Nichts gefunden.</div>}
          {!shown.length && !q && <div className="text-muted small">Keine Fälle für diese Auswahl{fStation ? ` (${fStation})` : ''}{fDate ? `, ${fDate.split('-').reverse().join('.')}` : ''}.</div>}
          {shown.map((c) => (
            <div key={c.id} className="card mb-2">
              <div className="card-body py-2 d-flex gap-3 align-items-center">
                {c.photo && <img src={withToken(`${getApiBaseUrl()}/identity-check-photos/${c.photo}`)} alt="" onClick={() => setZoom(withToken(`${getApiBaseUrl()}/identity-check-photos/${c.photo}`))}
                  style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, cursor: 'zoom-in' }} />}
                <div className="flex-grow-1">
                  <div><strong>Nr. {c.tag_number}</strong> · {c.station || '–'}{c.item_type ? ` · ${c.item_type} ${c.item_color || ''}` : ''}</div>
                  <div className="small text-muted">{c.guest_name} · {when(c.created_at)} · {c.staff_name}</div>
                  {list.manager && c.doc_type && <div className="small text-muted">{c.doc_type} {c.doc_number} · {c.guest_street}, {c.guest_city}</div>}
                </div>
                {list.manager && <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => openDetail(c.id)}>Details</button>}
              </div>
            </div>
          ))}
        </>
      )}

      {step > 0 && (
        <div className="card shadow-sm">
          <div className="card-body">
            <div className="progress mb-3" style={{ height: 6 }}><div className="progress-bar" style={{ width: `${Math.round(step * 33.4)}%` }} /></div>

            {step === 1 && (<>
              <h5>1 · Gepäckstück gefunden</h5>
              <p className="text-muted small">Vorher: Lass dir das Gepäck vom Gast beschreiben (Art, Farbe, Inhalt) und such es erst dann – aufschreiben musst du das nicht.</p>
              <label className="form-label fw-semibold">Foto vom Gepäckstück</label>
              <input type="file" accept="image/*" capture="environment" className="form-control" onChange={onPhoto} />
              {photoUrl && <img src={photoUrl} alt="" className="mt-2" style={{ maxWidth: '100%', maxHeight: 260, borderRadius: 10 }} />}
              <div className="row g-2 mt-2">
                <div className="col-7"><label className="form-label fw-semibold">Nummer vom Gepäckzettel</label>
                  <input className="form-control form-control-lg" inputMode="numeric" value={f.tagNumber} onChange={set('tagNumber')} /></div>
                <div className="col-5"><label className="form-label fw-semibold">Station</label>
                  <select className="form-select form-select-lg" value={f.station} onChange={set('station')}>
                    <option value="">–</option>{['G1', 'G5', 'G8', 'G10'].map((s) => <option key={s}>{s}</option>)}
                  </select></div>
                <div className="col-7"><label className="form-label fw-semibold">Tag der Abgabe</label>
                  <input type="date" className="form-control" value={f.dropoffDate} max={ctx.today} onChange={set('dropoffDate')} /></div>
              </div>
              <div className="form-check mt-3">
                <input className="form-check-input" type="checkbox" id="idc-match" checked={f.descriptionMatches} onChange={set('descriptionMatches')} />
                <label className="form-check-label" htmlFor="idc-match">Der Gast hat das Gepäck vor dem Suchen beschrieben – <strong>die Beschreibung passt</strong>.</label>
              </div>
              <div className="alert alert-warning small mt-3 mb-0">Passt es nicht oder bist du unsicher: <strong>nicht herausgeben</strong> und die Einsatzleitung anrufen.</div>
            </>)}

            {step === 2 && (<>
              <h5>2 · Personendaten &amp; Ausweis</h5>
              <label className="form-label fw-semibold">Name</label><input className="form-control mb-2" value={f.guestName} onChange={set('guestName')} />
              <label className="form-label fw-semibold">Straße + Nr.</label><input className="form-control mb-2" value={f.guestStreet} onChange={set('guestStreet')} />
              <label className="form-label fw-semibold">PLZ / Ort, Land</label><input className="form-control mb-2" value={f.guestCity} onChange={set('guestCity')} />
              <div className="row g-2">
                <div className="col-6"><label className="form-label fw-semibold">Geburtsdatum</label><BirthdateInput value={f.guestBirthdate} onChange={(iso) => setF((cur) => ({ ...cur, guestBirthdate: iso }))} /></div>
                <div className="col-6"><label className="form-label fw-semibold">Telefon</label><input type="tel" className="form-control" value={f.guestPhone} onChange={set('guestPhone')} /></div>
              </div>
              <label className="form-label fw-semibold mt-3">Ausweisdokument</label>{chips(ctx.docTypes, 'docType')}
              <label className="form-label fw-semibold mt-3">Dokumentnummer</label>
              <input className="form-control" autoCapitalize="characters" value={f.docNumber} onChange={set('docNumber')} />
              <div className="form-text">Bitte nur die Nummer abschreiben – den Ausweis <strong>nicht fotografieren</strong>.</div>
              <div className="form-check mt-3">
                <input className="form-check-input" type="checkbox" id="idc-ver" checked={f.idVerified} onChange={set('idVerified')} />
                <label className="form-check-label" htmlFor="idc-ver">Ich habe den Ausweis gesehen – <strong>das Foto passt zur Person</strong>.</label>
              </div>
            </>)}

            {step === 3 && (<>
              <h5>3 · Erklärung &amp; Unterschriften</h5>
              <div className="p-3 bg-light rounded mb-3 fst-italic">„Ich versichere, dass das Gepäckstück mein Eigentum ist und erkläre, dass falsche Angaben rechtliche Folgen haben können."</div>
              <div className="small text-muted mb-2">Nr. {f.tagNumber} · {f.station} · {f.guestName}</div>
              <SignaturePad label="Unterschrift Gast" onChange={setGuestSig} />
              <SignaturePad label={`Unterschrift Mitarbeiter (${ctx.staffName})`} onChange={setStaffSig} />
              <label className="form-label fw-semibold">Sonstiges (optional)</label>
              <input className="form-control" value={f.notes} onChange={set('notes')} />
            </>)}

            <div className="d-flex gap-2 mt-4">
              <button className="btn btn-outline-secondary" onClick={() => setStep(step - 1)} disabled={busy}>{step === 1 ? 'Abbrechen' : '← Zurück'}</button>
              {step < 3
                ? <button className="btn btn-primary flex-grow-1" disabled={!canNext()} onClick={() => setStep(step + 1)}>Weiter →</button>
                : <button className="btn btn-success flex-grow-1" disabled={busy || !guestSig || !staffSig} onClick={submit}>{busy ? 'Wird gespeichert …' : '✓ Speichern & Gepäck ausgeben'}</button>}
            </div>
          </div>
        </div>
      )}

      {detail && (
        <div onClick={() => setDetail(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 1900, overflowY: 'auto', padding: 12 }}>
          <div className="card mx-auto" style={{ maxWidth: 620 }} onClick={(e) => e.stopPropagation()}>
            <div className="card-body">
              <div className="d-flex align-items-start">
                <h5 className="me-auto">Nr. {detail.tag_number} · {detail.station || '–'} <span className="text-muted small">#I{detail.id}</span></h5>
                <button type="button" className="btn-close" onClick={() => setDetail(null)} />
              </div>
              {detail.photo && <img src={withToken(`${getApiBaseUrl()}/identity-check-photos/${detail.photo}`)} alt="" onClick={() => setZoom(withToken(`${getApiBaseUrl()}/identity-check-photos/${detail.photo}`))} style={{ width: '100%', maxHeight: 280, objectFit: 'contain', background: '#f3f3f3', borderRadius: 8, cursor: 'zoom-in' }} />}
              <table className="table table-sm mt-3 mb-2"><tbody>
                {[['Ausgegeben', `${when(detail.created_at)} Uhr · ${detail.staff_name || '–'}`], ['Tag der Abgabe', dDE(detail.dropoff_date)], ['Name', detail.guest_name], ['Adresse', `${detail.guest_street || ''}, ${detail.guest_city || ''}`], ['Geburtsdatum', dDE(detail.guest_birthdate)], ['Telefon', detail.guest_phone || '–'], ['Ausweis', `${detail.doc_type || ''} ${detail.doc_number || ''}`], ['Ausweis geprüft', detail.id_verified ? 'Ja – Foto passt zur Person' : 'Nein'], ['Vorab beschrieben', detail.description_matches ? 'Ja – Beschreibung passt' : 'Nein'], ['Sonstiges', detail.notes || '–']]
                  .map(([l, v]) => <tr key={l}><td className="text-muted" style={{ width: 150 }}>{l}</td><td>{v}</td></tr>)}
              </tbody></table>
              <div className="row g-2">
                {[['Unterschrift Gast', detail.guest_signature], ['Unterschrift Mitarbeiter', detail.staff_signature]].map(([l, src]) => (
                  <div className="col-6" key={l}><div className="small text-muted">{l}</div>{src ? <img src={src} alt={l} style={{ width: '100%', border: '1px solid #ddd', borderRadius: 6, background: '#fff' }} /> : '–'}</div>))}
              </div>
              <div className="small text-muted mt-3">Wird einen Monat nach Ende des Oktoberfestes automatisch gelöscht.</div>
            </div>
          </div>
        </div>
      )}

      {zoom && (
        <div onClick={() => setZoom(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}>
          <img src={zoom} alt="" style={{ maxWidth: '96vw', maxHeight: '96vh' }} />
        </div>
      )}
    </div>
  );
};

export default IdentityCheck;
